const getManagementApiSecret = () => process.env.REACT_APP_MANAGEMENT_API_SECRET || ''

export interface AnnouncementItem {
  announcementId: number
  title: string
  type: 'PROVIDER' | 'GENERAL' | 'SYSTEM_UPDATE'
  content: string
  effectiveDate: string | null
  expiryDate: string | null
  showAsPopup: boolean
  status: string
  createdAt: string
  updatedAt: string
  updatedBy?: string
}

const MANAGEMENT_API = (window as any).REACT_APP_MANAGEMENT_API || 'https://customer.cliniter.com/api'

const READ_IDS_STORAGE_KEY = 'cliniter_announcement_read_ids'
const SHOW_ON_LOGIN_FLAG_KEY = 'cliniter_announcement_show_on_login'

/** ตั้งค่าให้แสดง popup ทุกครั้งหลังเข้าสู่ระบบ (เรียกเมื่อ login สำเร็จ) */
export function setShowAnnouncementsOnLogin(): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(SHOW_ON_LOGIN_FLAG_KEY, '1')
  } catch {
    /* ignore */
  }
}

/** ล้าง flag แสดง popup หลัง login (เรียกเมื่อผู้ใช้ปิด popup ครบแล้ว) */
export function clearShowAnnouncementsOnLogin(): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(SHOW_ON_LOGIN_FLAG_KEY)
  } catch {
    /* ignore */
  }
}

/** เช็คว่าต้องแสดง popup หลัง login ล่าสุดหรือไม่ */
export function shouldShowAnnouncementsOnLogin(): boolean {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SHOW_ON_LOGIN_FLAG_KEY) === '1'
  } catch {
    return false
  }
}

function getReadIdsFromStorage(): number[] {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(READ_IDS_STORAGE_KEY) : null
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function setReadIdsInStorage(ids: number[]): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(READ_IDS_STORAGE_KEY, JSON.stringify(ids))
    }
  } catch {
    /* ignore */
  }
}

function addReadIdToStorage(id: number): void {
  const current = getReadIdsFromStorage()
  if (current.includes(id)) return
  setReadIdsInStorage([...current, id])
}

export async function fetchActiveAnnouncements(): Promise<AnnouncementItem[]> {
  try {
    const apiSecret = getManagementApiSecret()
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(apiSecret && { Authorization: `Bearer ${apiSecret}` })
    }
    const resp = await fetch(`${MANAGEMENT_API}/announcements/active`, { headers })
    if (!resp.ok) return []
    const json = await resp.json()
    return json?.data ?? json ?? []
  } catch {
    return []
  }
}

/** ดึงข่าวประกาศย้อนหลัง (รายการทั้งหมดหรือตาม type) สำหรับหน้า "ดูย้อนหลังข่าวประกาศ" */
export async function fetchAnnouncementHistory(options?: { type?: 'PROVIDER' | 'GENERAL' | 'SYSTEM_UPDATE' }): Promise<AnnouncementItem[]> {
  try {
    const apiSecret = getManagementApiSecret()
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(apiSecret && { Authorization: `Bearer ${apiSecret}` })
    }
    const params = new URLSearchParams()
    if (options?.type) params.set('type', options.type)
    const qs = params.toString()
    const url = `${MANAGEMENT_API}/announcements${qs ? `?${qs}` : ''}`
    const resp = await fetch(url, { headers })
    if (!resp.ok) return []
    const json = await resp.json()
    const list = json?.data ?? json ?? []
    return Array.isArray(list) ? list : []
  } catch {
    return []
  }
}

/** ดึงรายการ announcementId ที่อ่านแล้วจาก API แล้ว sync ลง localStorage (โหลดทุกครั้งที่ login) */
export async function fetchReadAnnouncementIds(userId?: number): Promise<number[]> {
  try {
    if (userId == null) return getReadIdsFromStorage()
    const apiSecret = getManagementApiSecret()
    const hostname = typeof window !== 'undefined' ? window.location?.hostname || '' : ''
    const siteName = (hostname ? hostname.split('.')[0] || hostname : 'unknown').toUpperCase()
    const deviceName = typeof window !== 'undefined' ? window.location?.hostname || navigator?.platform || 'web' : 'web'
    const params = new URLSearchParams({ siteName, deviceName, userId: String(userId) })
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(apiSecret && { Authorization: `Bearer ${apiSecret}` }),
    }
    const resp = await fetch(`${MANAGEMENT_API}/announcements/read?${params}`, { headers })
    if (!resp.ok) return getReadIdsFromStorage()
    const json = await resp.json()
    const readIds = json?.data?.readIds ?? json?.readIds
    const ids = Array.isArray(readIds) ? readIds : []
    setReadIdsInStorage(ids)
    return ids
  } catch {
    return getReadIdsFromStorage()
  }
}

/** เช็คว่าประกาศนี้ถือว่าอ่านแล้วหรือยัง (ใช้ชุด id ที่ได้จาก API) */
export function isDismissed(id: number, readIds: Set<number>): boolean {
  return readIds.has(id)
}

/** บันทึกว่าอ่านประกาศแล้ว: เก็บลง localStorage ทันที + ส่งไป API (fire-and-forget) */
export function recordAnnouncementRead(announcementId: number, userId: number, readBy: string): void {
  addReadIdToStorage(announcementId)

  const apiSecret = getManagementApiSecret()
  if (!apiSecret) return
  const hostname = typeof window !== 'undefined' ? window.location?.hostname || '' : ''
  const siteName = (hostname ? hostname.split('.')[0] || hostname : 'unknown').toUpperCase()
  const deviceName = typeof window !== 'undefined' ? window.location?.hostname || navigator?.platform || 'web' : 'web'
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${apiSecret}`,
  }

  fetch(`${MANAGEMENT_API}/announcements/${announcementId}/read`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      siteName,
      deviceName,
      userId,
      readBy,
      readAt: new Date().toISOString()
    }),
  }).catch(() => { /* ignore network errors */ })
}

export function dismissAnnouncement(id: number, userId: number, readBy: string) {
  recordAnnouncementRead(id, userId, readBy)
}

export const ANNOUNCEMENT_TYPES: Record<string, string> = {
  PROVIDER: 'ประกาศข่าวสารผู้ให้บริการ',
  GENERAL: 'ประกาศทั่วไป',
  SYSTEM_UPDATE: 'แจ้งเตือนการอัพเดทระบบ',
}

